import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, MaskLines, Eyebrow, SectionHead, HUDFrame } from "@/components/anim";
import { SECTORS } from "@/lib/content";

function PageHero() {
  return (
    <section className="relative overflow-hidden hud-grid noise pt-36 md:pt-44 pb-16 md:pb-24">
      <div className="pointer-events-none absolute -top-32 left-1/3 h-[440px] w-[440px] rounded-full bg-kinetic/10 blur-[130px]" />
      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <MaskLines
          lines={[
            <span key="e" className="font-hud text-[11px] md:text-xs uppercase tracking-[0.3em] text-kinetic">
              [ SOLUTIONS ]
            </span>,
          ]}
        />
        <MaskLines
          className="mt-6 font-display font-extrabold tracking-[-0.02em] leading-[0.98] text-[clamp(2.4rem,6vw,5.5rem)]"
          lines={["SIX SECTORS.", <span key="2">ONE <span className="text-kinetic">PLATFORM.</span></span>]}
          delay={0.2}
        />
        <MaskLines
          className="mt-7 max-w-2xl text-base md:text-lg text-[#9CA3AF] leading-relaxed"
          lines={[
            <span key="s" className="block">
              Solutions that move industries — one adaptable flight, data and talent platform.
            </span>,
          ]}
          delay={0.55}
        />
      </div>
    </section>
  );
}

function SectorRow({ sector, flip }) {
  return (
    <div
      className={`grid gap-10 lg:grid-cols-2 lg:items-center ${flip ? "lg:[direction:rtl]" : ""}`}
      data-testid={`sector-card-${sector.id}`}
    >
      <Reveal className="lg:[direction:ltr]">
        <HUDFrame testid={`sector-image-${sector.id}`}>
          <div className="overflow-hidden group">
            <img
              src={sector.img}
              alt={`${sector.name} drone operations`}
              className="h-[300px] md:h-[420px] w-full object-cover img-treat transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-void/60 via-transparent to-transparent" />
            <span className="absolute bottom-4 left-4 font-hud text-[10px] tracking-[0.24em] text-kinetic/90">
              MISSION FOOTAGE — {sector.name}
            </span>
          </div>
        </HUDFrame>
      </Reveal>

      <div className="lg:[direction:ltr]">
        <Reveal>
          <div className="flex items-baseline gap-4">
            <span className="font-display text-5xl md:text-6xl font-extrabold text-white/10">{sector.index}</span>
            <div>
              <h3 className="font-display text-2xl md:text-4xl font-bold tracking-tight">{sector.name}</h3>
              <p className="mt-1 font-hud text-xs uppercase tracking-[0.24em] text-kinetic">{sector.service}</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <ul className="mt-7 space-y-3">
            {sector.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-[#D1D5DB]">
                <Check className="mt-1 h-4 w-4 shrink-0 text-kinetic" />
                {b}
              </li>
            ))}
          </ul>
        </Reveal>
        {sector.signal && (
          <Reveal delay={0.16}>
            <div className="mt-7 rounded-xl border border-kinetic/25 bg-kinetic/5 p-5" data-testid={`sector-signal-${sector.id}`}>
              <p className="font-hud text-[10px] uppercase tracking-[0.24em] text-kinetic">DATA SIGNAL</p>
              <p className="mt-2 text-sm leading-relaxed text-[#E5E7EB]">{sector.signal}</p>
              <p className="mt-2 font-hud text-[10px] tracking-[0.16em] text-[#6B7280]">SOURCE: {sector.signalSource}</p>
            </div>
          </Reveal>
        )}
        <Reveal delay={0.2}>
          <Link
            to="/contact"
            data-testid={`sector-cta-${sector.id}`}
            className="group mt-8 inline-flex items-center gap-3 font-hud text-xs font-semibold uppercase tracking-[0.2em] text-kinetic"
          >
            Plan a mission
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
      </div>
    </div>
  );
}

export default function Solutions() {
  return (
    <main>
      <PageHero />
      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl space-y-24 px-5 md:px-8 md:space-y-32">
          {SECTORS.map((s, i) => (
            <SectorRow key={s.id} sector={s} flip={i % 2 === 1} />
          ))}
        </div>
      </section>
    </main>
  );
}
